import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FiArrowLeft, FiShoppingBag } from "react-icons/fi";
import { toast } from "react-toastify";
import { useQueryClient } from "react-query";
import useUserById from "../../../marketplace_admin/libs/usMerchantdetail";
import useBusinessMerchant from "../../../marketplace_admin/libs/useBusinessMerchant";
import useVerifyMerchant from "../../../marketplace_admin/libs/useVerifyMerchant";
import useRevokeMerchantInfo from "../../../marketplace_admin/libs/useRevokeMerchantInfo";
import useRejectMerchant from "../../../marketplace_admin/libs/useRejectMerchantInfo";
import useBlockUser from "../../../marketplace_admin/libs/useBlockUser";
import useUnblockUser from "../../../marketplace_admin/libs/useUnblockUser";
import PageHeader from "../../components/PageHeader";
import DetailGrid from "../../components/DetailGrid";
import YesNoBadge from "../../components/YesNoBadge";
import ConfirmModal from "../../components/ConfirmModal";
import useAdminMerchantStats from "../../../marketplace_admin/libs/useAdminMerchantStats";
import StatCard from "../../components/StatCard";
import { formatNgn } from "../../../Routes/Marketplace/libs/orderDisplayUtils";
import Spinner from "../../../Spinner/Spinner";

const AdminMerchantDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [modal, setModal] = useState(null);
  const [statsPeriod, setStatsPeriod] = useState("month");

  const { data: user, error: userError, isLoading: userLoading } = useUserById(id);
  const { data: business, isLoading: businessLoading } = useBusinessMerchant(id);
  const { data: merchantStats, isLoading: statsLoading } = useAdminMerchantStats(id, statsPeriod);

  const verifyMerchant = useVerifyMerchant();
  const revokeMerchant = useRevokeMerchantInfo();
  const rejectMerchant = useRejectMerchant();
  const blockUser = useBlockUser();
  const unblockUser = useUnblockUser();

  const invalidate = () => {
    queryClient.invalidateQueries(["user", id]);
    queryClient.invalidateQueries(["businessMerchant", id]);
    queryClient.invalidateQueries("merchants");
  };

  const closeModal = () => setModal(null);

  const handleVerify = async () => {
    try {
      await verifyMerchant.mutateAsync(id);
      invalidate();
      toast.success("Merchant approved");
      closeModal();
    } catch (err) {
      toast.error(err.message || "Verification failed");
    }
  };

  const handleRevoke = async () => {
    try {
      await revokeMerchant.mutateAsync(id);
      invalidate();
      toast.success("Verification revoked");
      closeModal();
    } catch (err) {
      toast.error(err.message || "Revoke failed");
    }
  };

  const handleReject = async () => {
    try {
      await rejectMerchant.mutateAsync(id);
      toast.success("Merchant removed");
      closeModal();
      navigate("/admin/merchants");
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Remove failed");
    }
  };

  const handleBlock = async () => {
    try {
      await blockUser.mutateAsync({
        userId: id,
        blockData: {
          startDate: new Date().toISOString(),
          endDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
        },
      });
      invalidate();
      toast.success("Merchant blocked");
      closeModal();
    } catch (err) {
      toast.error(err.message || "Block failed");
    }
  };

  const handleUnblock = async () => {
    try {
      await unblockUser.mutateAsync({ userId: id });
      invalidate();
      toast.success("Merchant unblocked");
      closeModal();
    } catch (err) {
      toast.error(err.message || "Unblock failed");
    }
  };

  const modalLoading =
    verifyMerchant.isLoading ||
    revokeMerchant.isLoading ||
    rejectMerchant.isLoading ||
    blockUser.isLoading ||
    unblockUser.isLoading;

  const fullName = user ? [user.first_name, user.last_name].filter(Boolean).join(" ") : "";
  const isRemoved =
    Boolean(user?.deletedAt) || String(user?.email || "").includes("@deleted.local");

  return (
    <>
      <Link to="/admin/merchants" className="dashboard-back-link">
        <FiArrowLeft aria-hidden="true" /> Back to merchants
      </Link>

      <PageHeader
        title={fullName || "Merchant details"}
        subtitle={user?.email}
        icon={FiShoppingBag}
        actions={
          user && (
            <>
              {!isRemoved && !user.isInfoVerify && (
                <button
                  type="button"
                  className="dashboard-btn dashboard-btn--primary"
                  onClick={() => setModal("verify")}
                >
                  Approve
                </button>
              )}
              {!isRemoved && (
                <>
                  <button type="button" className="dashboard-btn dashboard-btn--secondary" onClick={() => setModal("revoke")}>
                    Revoke
                  </button>
                  <Link to={`/admin/merchants/${id}/products`} className="dashboard-btn dashboard-btn--secondary">
                    View products
                  </Link>
                  {user.blocked ? (
                    <button type="button" className="dashboard-btn dashboard-btn--secondary" onClick={() => setModal("unblock")}>
                      Unblock
                    </button>
                  ) : (
                    <button type="button" className="dashboard-btn dashboard-btn--danger" onClick={() => setModal("block")}>
                      Block
                    </button>
                  )}
                </>
              )}
              <button type="button" className="dashboard-btn dashboard-btn--danger" onClick={() => setModal("reject")}>
                {isRemoved ? "Remove from records" : "Remove"}
              </button>
              <button type="button" className="dashboard-btn dashboard-btn--secondary" onClick={() => window.print()}>
                Print
              </button>
            </>
          )
        }
      />

      {userLoading && <Spinner />}
      {userError && (
        <div className="dashboard-card">
          <p style={{ color: "var(--dash-danger)", margin: 0 }}>Failed to load merchant.</p>
        </div>
      )}

      {user && isRemoved && (
        <div className="dashboard-banner dashboard-banner--warning" role="status">
          This merchant account has been removed. Their products and business profile have been cleared.
        </div>
      )}

      {user && (
        <div className="dashboard-card">
          <h2 className="dashboard-card__title">Account</h2>
          <DetailGrid
            items={[
              { label: "ID", value: user._id?.slice(0, 8).toUpperCase() },
              { label: "First name", value: user.first_name },
              { label: "Last name", value: user.last_name },
              { label: "Email", value: user.email },
              { label: "Phone", value: [user.country_code, user.phone_number].filter(Boolean).join(" ") },
              { label: "Verified", value: <YesNoBadge value={user.isInfoVerify} trueText="Verified" falseText="Pending" falseVariant="pending" /> },
              { label: "Active", value: <YesNoBadge value={user.isActive} /> },
              { label: "Blocked", value: <YesNoBadge value={user.blocked} variant="blocked" /> },
              {
                label: "Registered",
                value: user.registeredAt || user.createdAt
                  ? new Date(user.registeredAt || user.createdAt).toLocaleString()
                  : "—",
              },
            ]}
          />
        </div>
      )}

      {merchantStats && (
        <div className="dashboard-card">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 16 }}>
            <h2 className="dashboard-card__title" style={{ margin: 0 }}>Performance metrics</h2>
            <div className="dashboard-filter-pills">
              {["day", "week", "month", "year"].map((period) => (
                <button
                  key={period}
                  type="button"
                  className={statsPeriod === period ? "active" : ""}
                  onClick={() => setStatsPeriod(period)}
                >
                  {period.charAt(0).toUpperCase() + period.slice(1)}
                </button>
              ))}
            </div>
          </div>
          {statsLoading ? (
            <Spinner />
          ) : (
            <>
              <div className="dashboard-stat-grid">
                <StatCard label="Orders (period)" value={merchantStats.totals?.orders ?? 0} />
                <StatCard label="Delivered (period)" value={merchantStats.totals?.delivered ?? 0} />
                <StatCard label="Missed (period)" value={merchantStats.totals?.missed ?? 0} />
                <StatCard label="Sales (period)" value={formatNgn(merchantStats.totals?.sales || 0)} />
                <StatCard label="All-time orders" value={merchantStats.allTime?.orders ?? 0} />
                <StatCard label="All-time sales" value={formatNgn(merchantStats.allTime?.sales || 0)} />
                <StatCard label="Products" value={merchantStats.allTime?.productCount ?? 0} />
                <StatCard label="Pending approval" value={merchantStats.allTime?.pendingApprovalCount ?? 0} />
              </div>
              {merchantStats.reviewSummary?.count > 0 && (
                <p style={{ margin: "12px 0 0", color: "var(--dash-muted)" }}>
                  Store rating: {merchantStats.reviewSummary.avgRating?.toFixed(1)} ({merchantStats.reviewSummary.count} reviews)
                </p>
              )}
            </>
          )}
        </div>
      )}

      {businessLoading && <Spinner />}
      {!businessLoading && !business && (
        <div className="dashboard-card">
          <p style={{ color: "var(--dash-muted)", margin: 0 }}>No business profile on file.</p>
        </div>
      )}

      {business && (
        <div className="dashboard-card">
          <h2 className="dashboard-card__title">Business information</h2>
          <DetailGrid
            items={[
              { label: "Business name", value: business.businessName },
              { label: "Business type", value: business.businessType },
              { label: "Business address", value: business.businessAddress },
              { label: "Contact person", value: business.contactPerson },
              { label: "Contact email", value: business.contactEmail },
              { label: "Contact phone", value: business.contactPhoneNumber },
              { label: "Bank account", value: business.bankAccountDetails },
              { label: "Tax ID", value: business.taxIdentificationNumber },
              { label: "Business license", value: business.businessLicenseOrPermit },
              { label: "GST registration", value: business.goodsAndServicesTaxRegistration },
              { label: "VAT registration", value: business.valueAddedTaxRegistration },
              { label: "Customs ID", value: business.customsIdentificationNumber },
              { label: "Description", value: business.businessDescription },
              { label: "Products", value: business.productInformation },
              { label: "Returns policy", value: business.returnsAndRefundsPolicy },
              { label: "Support", value: business.customerSupportInformation },
            ]}
          />
        </div>
      )}

      <ConfirmModal
        open={modal === "verify"}
        title="Approve merchant"
        message="This merchant will be marked as verified and can operate on the marketplace."
        confirmLabel="Approve"
        loading={modalLoading}
        onConfirm={handleVerify}
        onCancel={closeModal}
      />
      <ConfirmModal
        open={modal === "revoke"}
        title="Revoke verification"
        message="This will remove verified status from the merchant's profile."
        confirmLabel="Revoke"
        danger
        loading={modalLoading}
        onConfirm={handleRevoke}
        onCancel={closeModal}
      />
      <ConfirmModal
        open={modal === "reject"}
        title={isRemoved ? "Remove merchant record" : "Remove merchant"}
        message={
          isRemoved
            ? "This merchant is already deactivated. Confirm to finish cleanup and return to the merchants list."
            : "This permanently removes the merchant account, their products, and business profile. This cannot be undone."
        }
        confirmLabel="Remove"
        danger
        loading={modalLoading}
        onConfirm={handleReject}
        onCancel={closeModal}
      />
      <ConfirmModal
        open={modal === "block"}
        title="Block merchant"
        message="The merchant will not be able to sign in until unblocked."
        confirmLabel="Block"
        danger
        loading={modalLoading}
        onConfirm={handleBlock}
        onCancel={closeModal}
      />
      <ConfirmModal
        open={modal === "unblock"}
        title="Unblock merchant"
        message="Restore sign-in access for this merchant?"
        confirmLabel="Unblock"
        loading={modalLoading}
        onConfirm={handleUnblock}
        onCancel={closeModal}
      />
    </>
  );
};

export default AdminMerchantDetailPage;
